import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, Upload } from 'lucide-react'
import type { PersonalDocumentEmployeeResponse } from '@/api/model'
import { Button } from '@/components/shadcn/button'

interface Props {
  employee: PersonalDocumentEmployeeResponse
  onView: (employee: PersonalDocumentEmployeeResponse) => void
  /** File selection for exactly this employee (assignment explicit, no personnel number needed). */
  onUpload: (files: FileList, employee: PersonalDocumentEmployeeResponse) => void
}

/** Tile of an employee: name, document count, view and direct upload (drag & drop / click). */
export function EmployeeCard({ employee, onView, onUpload }: Props) {
  const { t } = useTranslation('service-modules-personal-documents')
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true) }}
      onDragLeave={(e) => {
        e.preventDefault(); e.stopPropagation()
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false)
      }}
      onDrop={(e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(false)
        if (e.dataTransfer.files.length > 0) onUpload(e.dataTransfer.files, employee)
      }}
      className={`flex flex-col gap-3 rounded-lg border border-dashed p-3 transition-colors ${
        isDragging ? 'border-primary bg-primary/5' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">
            {employee.firstName} {employee.lastName}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('personalDocuments.documentsCount')}: {employee.documentCount ?? 0}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 flex-shrink-0"
          aria-label={t('personalDocuments.showEmployee')}
          onClick={() => onView(employee)}
        >
          <Eye className="size-4" />
        </Button>
      </div>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-primary hover:opacity-80"
      >
        <Upload className="size-3.5" />
        {t('personalDocuments.dropOrSelect')}<span className="underline">{t('personalDocuments.select')}</span>
      </button>

      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) onUpload(e.target.files, employee)
          e.target.value = ''
        }}
      />
    </div>
  )
}
